import { Box, Card, CardActionArea, Chip, Stack, Typography } from '@mui/material';
import PlaceRounded from '@mui/icons-material/PlaceRounded';
import { Link as RouterLink } from 'react-router';
import type { ProfileCard } from '@/api/types';
import { transition } from '@/theme';
import { placeLabel, priceLabel, schemeFor } from '../format';
import { useLabels } from '../api';
import { ProfileAvatar } from './ProfileAvatar';
import { ProfileBadges } from './ProfileBadges';

interface ProfileCardViewProps {
  card: ProfileCard;
  /** Preview mode (profile editor): not a link. */
  preview?: boolean;
}

/**
 * A professional in search results. The top band uses the professional's own
 * brand color: even in the directory, each one looks like themselves.
 */
export function ProfileCardView({ card, preview }: ProfileCardViewProps) {
  const labels = useLabels();
  const scheme = schemeFor(card.accentColor).light;
  const place = placeLabel(card);
  const onlineOnly = card.serviceModes.length > 0 && card.serviceModes.every((m) => m === 'online');
  const price = priceLabel(card.priceFromCents, card.priceUnit);

  const content = (
    <Stack sx={{ height: '100%' }}>
      <Box
        aria-hidden
        sx={{
          height: 64,
          background: `linear-gradient(120deg, ${scheme.primary.main}, ${scheme.primary.light} 55%, ${scheme.primary.container})`,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 85% -20%, rgba(255,255,255,0.45), transparent 45%)',
          },
        }}
      />
      <Stack spacing={1.5} sx={{ px: 2.5, pb: 2.5, mt: -4, flex: 1, position: 'relative' }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-end' }}>
          <ProfileAvatar
            name={card.displayName}
            photoUrl={card.photoUrl}
            accent={card.accentColor}
            size={72}
          />
          {price && (
            <Box sx={{ ml: 'auto', textAlign: 'right', pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" component="div">
                a partir de
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {price}
              </Typography>
            </Box>
          )}
        </Stack>
        <Box>
          <Typography variant="h6" component="h3" sx={{ lineHeight: 1.25 }}>
            {card.displayName}
          </Typography>
          {card.headline && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {card.headline}
            </Typography>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ alignItems: 'center', color: 'text.secondary' }}
        >
          <PlaceRounded sx={{ fontSize: 18 }} />
          <Typography variant="body2" noWrap>
            {onlineOnly
              ? 'Online, para todo o Brasil'
              : [card.neighborhoods.slice(0, 2).join(', '), place].filter(Boolean).join(' · ') ||
                'Local não informado'}
          </Typography>
        </Stack>
        <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {card.specialties.slice(0, 3).map((key) => (
            <Chip key={key} size="small" variant="outlined" label={labels.specialty[key] ?? key} />
          ))}
          {card.specialties.length > 3 && (
            <Chip size="small" variant="outlined" label={`+${card.specialties.length - 3}`} />
          )}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <ProfileBadges card={card} max={3} />
        {!card.acceptingClients && (
          <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
            Sem vagas no momento
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Card
      sx={(theme) => ({
        height: '100%',
        overflow: 'hidden',
        transition: transition(['transform', 'box-shadow', 'border-color'], 'medium1'),
        ...(!preview && {
          '&:hover': {
            transform: 'translateY(-3px)',
            borderColor: scheme.primary.main,
            boxShadow: `0 18px 40px -22px ${theme.alpha(scheme.primary.main, 0.7)}`,
          },
        }),
      })}
    >
      {preview ? (
        content
      ) : (
        <CardActionArea
          component={RouterLink}
          to={`/personal/${card.slug}`}
          sx={{ height: '100%', alignItems: 'stretch' }}
        >
          {content}
        </CardActionArea>
      )}
    </Card>
  );
}
